import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import PaymentIframe from './components/PaymentIframe';
import './styles/PaymentIframe.css';
import { WagmiProvider } from 'wagmi';
import { bsc, bscTestnet, base, baseSepolia } from 'wagmi/chains';
import { 
  getDefaultConfig, 
  RainbowKitProvider,
  lightTheme,
  darkTheme
} from '@rainbow-me/rainbowkit';
import { 
  binanceWallet, 
  coinbaseWallet, 
  metaMaskWallet, 
  trustWallet,
  walletConnectWallet
} from '@rainbow-me/rainbowkit/wallets';
import '@rainbow-me/rainbowkit/styles.css';
import { http } from 'wagmi';

// Global error handler to suppress WalletConnect proposal expired errors
if (typeof window !== 'undefined') {
  const originalConsoleError = console.error;
  console.error = function(...args) {
    const firstArg = args[0];
    const isWalletConnectHistoryRestore =
      firstArg &&
      typeof firstArg === 'object' &&
      firstArg.context === 'core/history' &&
      typeof firstArg.msg === 'string' &&
      firstArg.msg.includes('Restore will override. history');
    const isWalletConnectHistoryLog =
      firstArg &&
      typeof firstArg === 'object' &&
      firstArg.context === 'core/history' &&
      (typeof firstArg.msg === 'string' || typeof firstArg.message === 'string' || Number.isFinite(firstArg.level));

    // Check if this is a WalletConnect proposal expired error or testnetMode warning
    if ((args[0] && (
      (typeof args[0] === 'string' && (
        args[0].includes('Proposal expired') || 
        args[0].includes('React does not recognize the `testnetMode` prop')
      )) ||
      (args[0] instanceof Error && (
        args[0].message.includes('Proposal expired') ||
        args[0].message.includes('React does not recognize the `testnetMode` prop')
      ))
    )) || isWalletConnectHistoryRestore || isWalletConnectHistoryLog) {
      // Just log it at debug level instead
      console.debug('Suppressed warning:', ...args);
      return;
    }
    // Otherwise use the original console.error
    originalConsoleError.apply(console, args);
  };

  // Also suppress the uncaught error UI
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && event.reason.toString().includes('Proposal expired')) {
      event.preventDefault();
      return false;
    }
  }, true);
}

// Simple loading component
const Loading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <p>Loading payment system...</p>
  </div>
);

// Simple error component
const TabConflictError = () => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column',
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    padding: '0 20px',
    textAlign: 'center'
  }}>
    <h2 style={{ color: '#d32f2f' }}>Multiple Tabs Detected</h2>
    <p>For security reasons, please use only one PePay payment tab at a time.</p>
    <button 
      onClick={() => window.location.reload()}
      style={{
        marginTop: '20px',
        padding: '10px 20px',
        backgroundColor: '#2196f3',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      Reload This Tab
    </button>
  </div>
);

const LOCK_HEARTBEAT_MS = 2000;
const LOCK_STALE_MS = 8000;
const LOCK_HANDSHAKE_MS = 300;
const LOCK_VERIFY_DELAY_MS = 60;
const LOCK_CLOCK_SKEW_MS = 30000;

const getSafeStorage = (storageType) => {
  if (typeof window === 'undefined') return null;
  try {
    const storage = window[storageType];
    const testKey = '__pepay_storage_test__';
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return storage;
  } catch (error) {
    return null;
  }
};

const safeStorageGet = (storage, key) => {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch (error) {
    return null;
  }
};

const safeStorageSet = (storage, key, value) => {
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
};

const safeStorageRemove = (storage, key) => {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch (error) {}
};

const safeParseJson = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const getLockScope = () => {
  if (typeof window === 'undefined') return 'global';
  try {
    const params = new URLSearchParams(window.location.search);
    let scope = params.get('session') || params.get('invoice') || params.get('invoice_id');
    if (!scope && window.location.hash) {
      const hashQueryIndex = window.location.hash.indexOf('?');
      if (hashQueryIndex !== -1) {
        const hashParams = new URLSearchParams(window.location.hash.slice(hashQueryIndex + 1));
        scope = hashParams.get('session') || hashParams.get('invoice') || hashParams.get('invoice_id');
      }
    }
    return scope || null;
  } catch (error) {
    return null;
  }
};

const createTabId = () => `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

const getOrCreateTabId = (storage, key) => {
  const existing = safeStorageGet(storage, key);
  if (existing) return existing;
  const tabId = createTabId();
  safeStorageSet(storage, key, tabId);
  return tabId;
};

const PaymentIframeRoute = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const session = (params.get('session') || '').trim();
  const signature = (params.get('signature') || '').trim();
  const sessionKey = `${session || 'none'}:${signature || 'none'}`;
  return <PaymentIframe key={sessionKey} />;
};

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [config, setConfig] = useState(null);
  const [tabConflict, setTabConflict] = useState(false);
  const [isActiveTab, setIsActiveTab] = useState(false);
  
  useEffect(() => {
    const lockScope = getLockScope();
    if (!lockScope) {
      setTabConflict(false);
      setIsActiveTab(true);
      return undefined;
    }
    const lockKey = `pepay_active_tab:${lockScope}`;
    const tabIdKey = `pepay_tab_id:${lockScope}`;
    const channelName = `pepay_payment_tab:${lockScope}`;
    const localStore = getSafeStorage('localStorage');
    const sessionStore = getSafeStorage('sessionStorage');
    const tabId = sessionStore ? getOrCreateTabId(sessionStore, tabIdKey) : createTabId();
    let isCancelled = false;
    let isActive = false;
    let sawActive = false;
    let heartbeatInterval = null;
    let handshakeTimeout = null;
    let verifyTimeout = null;
    let releaseLock = null;
    let channel = null;

    const setConflict = () => {
      if (isCancelled) return;
      setTabConflict(true);
      setIsActiveTab(false);
    };

    const writeHeartbeat = () => {
      if (!localStore) return;
      const payload = JSON.stringify({ tabId, ts: Date.now() });
      safeStorageSet(localStore, lockKey, payload);
    };

    const claimActive = () => {
      if (isCancelled) return;
      isActive = true;
      setTabConflict(false);
      setIsActiveTab(true);
      writeHeartbeat();
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      heartbeatInterval = setInterval(writeHeartbeat, LOCK_HEARTBEAT_MS);
      if (channel) {
        try {
          channel.postMessage({ type: 'active', tabId });
        } catch (error) {}
      }
    };

    const hasFreshLock = () => {
      if (!localStore) return false;
      const data = safeParseJson(safeStorageGet(localStore, lockKey));
      if (!data || !data.ts || !data.tabId) return false;
      if (data.tabId === tabId) return false;
      const ts = Number(data.ts);
      if (!Number.isFinite(ts)) return false;
      if (ts > Date.now() + LOCK_CLOCK_SKEW_MS) return false;
      return Date.now() - ts < LOCK_STALE_MS;
    };

    const attemptClaim = () => {
      if (isCancelled || isActive) return;
      if (sawActive || hasFreshLock()) {
        setConflict();
        return;
      }
      if (localStore) {
        safeStorageSet(localStore, lockKey, JSON.stringify({ tabId, ts: Date.now() }));
      }
      verifyTimeout = setTimeout(() => {
        if (isCancelled || isActive) return;
        if (hasFreshLock()) {
          setConflict();
          return;
        }
        const current = safeParseJson(safeStorageGet(localStore, lockKey));
        if (current && current.tabId && current.tabId !== tabId) {
          setConflict();
          return;
        }
        claimActive();
      }, LOCK_VERIFY_DELAY_MS);
    };

    if (typeof BroadcastChannel !== 'undefined') {
      try {
        channel = new BroadcastChannel(channelName);
      } catch (error) {
        channel = null;
      }
    }

    if (channel) {
      channel.onmessage = (event) => {
        const message = event?.data;
        if (!message || typeof message !== 'object') return;
        if (message.type === 'ping') {
          if (isActive) {
            try {
              channel.postMessage({ type: 'active', tabId });
            } catch (error) {}
          }
        }
        if (message.type === 'active' && message.tabId !== tabId && !isActive) {
          sawActive = true;
          setConflict();
        }
      };
    }

    const useLocks = typeof navigator !== 'undefined' &&
      navigator.locks &&
      typeof navigator.locks.request === 'function';

    const startHandshake = () => {
      if (hasFreshLock()) {
        setConflict();
        return;
      }
      if (channel) {
        try {
          channel.postMessage({ type: 'ping', tabId });
        } catch (error) {}
        handshakeTimeout = setTimeout(() => {
          attemptClaim();
        }, LOCK_HANDSHAKE_MS);
      } else {
        attemptClaim();
      }
    };

    if (useLocks) {
      let release;
      const releasePromise = new Promise(resolve => {
        release = resolve;
      });
      releaseLock = release;
      navigator.locks.request(lockKey, { mode: 'exclusive', ifAvailable: true }, async (lock) => {
        if (isCancelled) return;
        if (!lock) {
          startHandshake();
          return;
        }
        claimActive();
        await releasePromise;
      }).catch(() => {
        startHandshake();
      });
    } else {
      startHandshake();
    }

    return () => {
      isCancelled = true;
      if (handshakeTimeout) clearTimeout(handshakeTimeout);
      if (verifyTimeout) clearTimeout(verifyTimeout);
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      if (isActive && localStore) {
        const current = safeParseJson(safeStorageGet(localStore, lockKey));
        if (current?.tabId === tabId) {
          safeStorageRemove(localStore, lockKey);
        }
      }
      if (channel) {
        if (isActive) {
          try {
            channel.postMessage({ type: 'release', tabId });
          } catch (error) {}
        }
        channel.close();
      }
      if (releaseLock) {
        releaseLock();
      }
    };
  }, []);

  useEffect(() => {
    if (!isActiveTab || tabConflict || isInitialized) return;
    const projectId = process.env.REACT_APP_WALLETCONNECT_ID || '7dccaaf269a91f7778a81b50a69d3533';

    // Create a new configuration with longer timeouts and better error handling
    const wagmiConfig = getDefaultConfig({
      appName: process.env.REACT_APP_APP_NAME || 'PePay Payments',
      projectId,
      chains: [bsc, bscTestnet, base, baseSepolia],
      transports: {
        // Define multiple fallback transports with longer timeouts
        [bsc.id]: http('https://bsc-dataseed1.binance.org', {
          timeout: 15000,
          fetchOptions: {
            mode: 'cors',
          },
        }),
        [bscTestnet.id]: http('https://data-seed-prebsc-1-s1.binance.org:8545', {
          timeout: 15000,
          fetchOptions: {
            mode: 'cors',
          }, 
        }),
        // Add Base network transports
        [base.id]: http('https://mainnet.base.org', {
          timeout: 15000,
          fetchOptions: {
            mode: 'cors',
          },
        }),
        [baseSepolia.id]: http('https://sepolia.base.org', {
          timeout: 15000,
          fetchOptions: {
            mode: 'cors',
          },
        }),
      },
      walletConnectOptions: {
        projectId,
        metadata: {
          name: process.env.REACT_APP_APP_NAME || 'PePay Commerce',
          description: process.env.REACT_APP_APP_DESCRIPTION || 'PePay Payment Protocol for AI Agents',
          url: process.env.REACT_APP_APP_URL || window.location.origin,
          // Use absolute URL for icon - if relative path, prepend the current origin
          icons: [
            process.env.REACT_APP_APP_ICON?.startsWith('http') 
              ? process.env.REACT_APP_APP_ICON 
              : `${window.location.origin}${process.env.REACT_APP_APP_ICON || '/pepay-logo-rounded-official.png'}`
          ]
        },
        // More aggressive timeout settings
        relayUrl: 'wss://relay.walletconnect.org',
        timeout: 120000, // 2 minutes
        qrModalOptions: {
          themeMode: 'light',
          explorerRecommendedWalletIds: [],
          // Prevent modal from closing automatically
          explorerExcludedWalletIds: 'ALL',
          // Enable mobile linking
          enableExplorer: true
        }
      },
      wallets: [
        {
          groupName: 'Recommended for BNB Chain',
          wallets: [
            binanceWallet,  // Put Binance Wallet first
            metaMaskWallet,
            trustWallet,
            coinbaseWallet,
            walletConnectWallet
          ]
        }
      ]
    });

    setConfig(wagmiConfig);
    setIsInitialized(true);
  }, [isActiveTab, isInitialized, tabConflict]);
  
  // If there's a tab conflict, show the error UI
  if (tabConflict) {
    return <TabConflictError />;
  }
  
  // Don't render anything until config is ready
  if (!config) {
    return <Loading />;
  }
  
  return (
    <WagmiProvider config={config}>
      <RainbowKitProvider
        theme={lightTheme({
          accentColor: '#F0B90B', // Binance yellow
          accentColorForeground: 'black',
          borderRadius: 'medium',
          fontStack: 'system',
          overlayBlur: 'small',
        })}
        modalSize="compact"
        showRecentTransactions={false}
        chains={[bsc, bscTestnet, base, baseSepolia]}
        appInfo={{
          appName: 'PePay Payments',
          learnMoreUrl: 'https://pepay.net/about',
        }}
      >
        <BrowserRouter>
          <Routes>
            <Route path="/pay" element={<PaymentIframeRoute />} />
          </Routes>
        </BrowserRouter>
      </RainbowKitProvider>
    </WagmiProvider>
  );
}

export default App;
